import { type ReactElement, type ReactNode, useEffect } from 'react';
import { RealtimeService } from './RealtimeService';
import { RealtimeServiceContext } from './context';
import { useUser } from 'services/session';
import type { RealtimeConnection } from 'modules/core/types';
import { fetcher } from 'services/api';
import { controllerFor } from './abort';

const realtimeService = new RealtimeService({
  /**
   * The connection credential is minted for the acting user. `null` means realtime is disabled
   * server-side for this deployment or identity.
   */
  loadConnection: (signal) =>
    fetcher<RealtimeConnection | null>({
      method: 'GET',
      url: 'realtime/connection/',
      abortController: controllerFor(signal),
    }),
  loadChannelToken: async (channel, signal) => {
    const response = await fetcher<{ token: string }>({
      method: 'GET',
      url: `realtime/token/${channel}/`,
      abortController: controllerFor(signal),
    });
    return response.token;
  },
});

if (import.meta.env.DEV) (window as any).__rt = realtimeService;

export function RealtimeProvider({ children }: { children: ReactNode }): ReactElement {
  const identityKey = useUser().id;

  useEffect(() => {
    realtimeService.start(identityKey);
    return () => realtimeService.stop();
  }, [identityKey]);

  return <RealtimeServiceContext.Provider value={realtimeService}>{children}</RealtimeServiceContext.Provider>;
}
